import ButtonV2, { getButtonClasses } from "components/elements/buttonV2"
import TextLockup from "components/elements/textLockup"
import { gsap } from "gsap"
import { Power0, Power2 } from "gsap/all"
import PropTypes from "prop-types"
import React, { createRef, useCallback, useEffect, useState } from "react"
import { mediumAnim } from "utils/gsapAnims"
import HELPERS from "utils/helpers"
import useWindowDimensions from "utils/useWindowDimensions"
import Card from "./card"
import "./testimonials.scss"

const Testimonials = ({ callToActionButton, cards, displayStyle, intro }) => {
  const [cardRefArray, setCardRefArray] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [animating, setIsAnimating] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const { width } = useWindowDimensions()
  const [renderableData, setRenderableData] = useState([])
  const shuffleDuration = mediumAnim
  const layoutStackWidthThreshold = 1376
  const numberOfCards = cards.length

  const currentCard = useCallback(() => {
    return cardRefArray[currentIndex]?.current
  }, [cardRefArray, currentIndex])

  const backgroundRenderables = useCallback(() => {
    return HELPERS.smartSlice(
      cardRefArray,
      (currentIndex + 1) % cards.length,
      cards.length - 1
    )
      .slice(0, Math.min(cards.length, cards.length) - 1)
      .map(c => c?.current)
  }, [cardRefArray, cards.length, currentIndex])

  const layoutCards = useCallback(() => {
    // this allows us to re-layout on a breakpoint change
    // while keeping the right order of z indexes
    const cardContainers = [currentCard()?.cardContainer].concat(
      backgroundRenderables().map(c => c?.cardContainer)
    )
    for (const [i, ref] of cardContainers.entries()) {
      if (ref) {
        gsap.set(ref, {
          x: i * cardOffset(),
          y: i * cardOffset(),
        })
      }
    }
  }, [backgroundRenderables, currentCard])

  const cardOffset = () => {
    return window.innerWidth <= layoutStackWidthThreshold ? 12 : 24
  }

  const animateToNextCard = e => {
    if (
      animating ||
      (e.type === "keydown" && e.keyCode !== 13 && e.keyCode !== 32)
    )
      return

    if (e.keyCode === 32) e.preventDefault()

    toggleForwardAnimation()
  }

  const animateToPreviousCard = e => {
    if (
      animating ||
      (e.type === "keydown" && e.keyCode !== 13 && e.keyCode !== 32)
    )
      return
    if (e.keyCode === 32) e.preventDefault()

    toggleBackwardAnimation()
  }

  const toggleForwardAnimation = () => {
    setIsAnimating(true)

    const topCard = currentCard()?.cardContainer
    const otherCards = backgroundRenderables().map(c => c?.cardContainer)

    //Animate the top most card top left and fade out
    gsap.to(topCard, {
      duration: shuffleDuration / 2,
      ease: Power2.easeIn,
      onComplete: slideOutForwardComplete,
      opacity: 0,
      x: -cardOffset() / 2,
      y: -cardOffset() / 2,
    })

    // Animate the other cards to their new position
    for (const [i, container] of otherCards.entries()) {
      gsap.to(container, {
        duration: shuffleDuration,
        ease: Power2.easeInOut,
        x: i * cardOffset(),
        y: i * cardOffset(),
      })
    }
  }

  const slideOutForwardComplete = () => {
    currentCard().cardContainer.style.zIndex = 0

    //Move the top card to back, fade back in
    gsap.set(currentCard()?.cardContainer, {
      x: cardOffset() * (cards.length - 1),
      y: cardOffset() * (cards.length - 1),
    })

    gsap.to(currentCard()?.cardContainer, {
      duration: shuffleDuration / 2,
      ease: Power2.easeOut,
      onComplete: updateCards,
      opacity: 1,
    })
  }

  const toggleBackwardAnimation = () => {
    setIsAnimating(true)

    //probably an easier way to get bottom card and an array of
    //the other cards but i'm just zoomin' along
    const topCard = currentCard()?.cardContainer
    const otherCards = backgroundRenderables().map(c => c?.cardContainer)
    const bottomCard = otherCards.pop()
    const allOtherCards = [topCard].concat(otherCards)

    //fade out bottom card
    gsap.to(bottomCard, {
      duration: shuffleDuration / 2,
      ease: Power2.easeIn,
      onComplete: fadeOutBackwardComplete,
      opacity: 0,
    })

    // Animate the other cards to their new position
    for (const [i, container] of allOtherCards.entries()) {
      gsap.to(container, {
        duration: shuffleDuration,
        ease: Power2.easeInOut,
        x: (i + 1) * cardOffset(),
        y: (i + 1) * cardOffset(),
      })
    }
  }

  const fadeOutBackwardComplete = () => {
    const otherCards = backgroundRenderables().map(c => c?.cardContainer)
    const bottomCard = otherCards.pop()
    bottomCard.style.zIndex = cards.length

    //Move the bottom card to front, fade back in
    gsap.set(bottomCard, {
      x: -cardOffset() / 4,
      y: -cardOffset() / 4,
    })

    gsap.to(bottomCard, {
      duration: shuffleDuration / 2.5,
      ease: Power0.easeNone,
      onComplete: updateCardsReverse,
      opacity: 1,
      x: 0,
      y: 0,
    })
  }

  const updateCards = () => {
    const newIndex = (currentIndex + 1) % cards.length
    setCurrentIndex(newIndex)
    setIsAnimating(false)
  }

  const updateCardsReverse = () => {
    let newIndex = currentIndex - 1
    if (newIndex < 0) {
      newIndex = cards.length - 1
    }
    setCurrentIndex(newIndex)
    setIsAnimating(false)
  }

  useEffect(() => {
    setCardRefArray(elRefs =>
      Array(numberOfCards)
        .fill()
        .map((_, i) => elRefs[i] || createRef())
    )
  }, [numberOfCards])

  useEffect(() => {
    if (cardRefArray.length < numberOfCards - 1) return
    layoutCards()
  }, [cardRefArray, layoutCards, numberOfCards])

  useEffect(() => {
    if (isMobile && width > layoutStackWidthThreshold) {
      setIsMobile(false)
    } else if (!isMobile && width <= layoutStackWidthThreshold) {
      setIsMobile(true)
    }
  }, [width, isMobile])

  useEffect(() => {
    layoutCards()
  }, [isMobile, layoutCards])

  useEffect(() => {
    setRenderableData(HELPERS.smartSlice(cards, currentIndex, cards.length))
  }, [currentIndex, cards])

  // Set CSS custom property for dynamic margin calculation
  useEffect(() => {
    const container = document.querySelector(".testimonial-v2__container")
    if (container) {
      container.style.setProperty("--card-count", numberOfCards.toString())
    }
  }, [numberOfCards])

  // A fifth card gets pastel green rather than repeating pastel pink.
  const colors = [
    "pastel-pink",
    "pastel-blue",
    "blue",
    "dark-yellow",
    "pastel-green",
  ]

  return (
    <section className="testimonial-v2">
      <div className="container-v2">
        <div className="testimonial-v2__header">
          {intro && (
            <TextLockup {...intro} alignment="left" className="text-lockup" />
          )}
          {callToActionButton &&
            callToActionButton.path &&
            callToActionButton.text && (
              <p className="testimonial-v2__cta">
                <ButtonV2
                  accessibilityLabel={callToActionButton.accessibilityLabel}
                  className={getButtonClasses(
                    callToActionButton.buttonStyle,
                    callToActionButton.buttonSize
                  )}
                  href={callToActionButton.path}
                  key={callToActionButton.id}
                  showArrow={
                    callToActionButton.showArrow === "Yes" ? true : false
                  }
                  text={callToActionButton.text}
                />
              </p>
            )}
        </div>

        <div className="testimonial-v2__container">
          {renderableData.map((card, localIndex) => {
            const realIndex = (currentIndex + localIndex) % cards.length
            const isCardVisible = realIndex === 0
            return (
              <Card
                {...card}
                ariaHidden={!isCardVisible}
                color={colors[realIndex % colors.length]}
                key={realIndex}
                onNext={animateToNextCard}
                onPrevious={animateToPreviousCard}
                plain={displayStyle === "Research Studies"}
                ref={cardRefArray[realIndex]}
                showNavigation={true}
                z={cards.length - localIndex}
              />
            )
          })}
        </div>
      </div>
    </section>
  )
}

Testimonials.props = {
  cards: PropTypes.arrayOf(PropTypes.shape(Card.props)),
  // "Research Studies" shows the cards without quote marks (see card.scss).
  displayStyle: PropTypes.string,
  intro: PropTypes.shape(TextLockup.props),
  callToActionButton: PropTypes.shape(ButtonV2.props),
}

Testimonials.propTypes = Testimonials.props

export default Testimonials
